import { map } from 'lodash';
import { useRef } from 'react';
import { ModalReviewFragment } from 'src/gqlReactTypings.generated.d';
import {
  ShefNumDeliveriesChip,
  ShefNumReviewsChip,
} from 'src/pages/consumer/order/components/ReviewsModal/ModalContent/ConsumerOrderShefChip';
import { ButtonSizes, PrimaryButton } from 'src/shared/design-system/Buttons';
import { Loading } from 'src/shared/design-system/Loading';
import { useScrollLock } from 'src/shared/hooks/useScrollLock';
import styled from 'styled-components';
import { Colors, FontSize, FontWeight, LineHeight, Spacing } from 'web-common/src/shared/styles';
import { ModalContentReview, ModalContentReviews } from './ModalContentReview';

const HeaderContainer = styled.div`
  align-items: center;
  border-bottom: 1px solid ${Colors.GRAY_LITE_20};
  display: flex;
  flex-direction: column;
  margin-top: ${Spacing.SINGLE_HALF};
  padding-bottom: ${Spacing.SINGLE};
  position: sticky;
`;

const ShefName = styled.p`
  font-weight: ${FontWeight.BOLD};
  font-size: ${FontSize.XL};
  line-height: ${LineHeight.L};
  margin-bottom: ${Spacing.HALF};
`;

const ChipsContainer = styled.div`
  display: flex;
  gap: ${Spacing.HALF};
`;

const ReviewsFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.SINGLE_HALF} 0 ${Spacing.DOUBLE_HALF};
`;

interface ModalContentProps {
  availableDishes: string[];
  isLoading: boolean;
  loadMoreReviews: () => void;
  numOrders: number;
  numRatings: number;
  reviews: ModalReviewFragment[];
  shefId: string;
  shefName: string;
  showLoadMoreButton: boolean;
  timeZone: string;
}

export const ModalContent = ({
  availableDishes,
  isLoading,
  loadMoreReviews,
  numOrders,
  numRatings,
  reviews,
  shefId,
  shefName,
  showLoadMoreButton,
  timeZone,
}: ModalContentProps): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  useScrollLock({
    enabled: true,
    scrollableTargetRef: ref,
  });

  return (
    <>
      <HeaderContainer>
        <ShefName>Shef {shefName}</ShefName>
        <ChipsContainer>
          <ShefNumReviewsChip numRatings={numRatings} isTrialShef={false} size='large' />
          <ShefNumDeliveriesChip numOrders={numOrders} size='large' />
        </ChipsContainer>
      </HeaderContainer>
      <ModalContentReviews ref={ref}>
        {map<ModalReviewFragment>(reviews, (review: ModalReviewFragment) => (
          <ModalContentReview
            availableDishes={availableDishes}
            key={review.id}
            review={review}
            shefId={shefId}
            timeZone={timeZone}
          />
        ))}

        {!isLoading && showLoadMoreButton && (
          <ReviewsFooter>
            <PrimaryButton size={ButtonSizes.M} onClick={loadMoreReviews}>
              Load more reviews
            </PrimaryButton>
          </ReviewsFooter>
        )}
      </ModalContentReviews>

      {isLoading && <Loading />}
    </>
  );
};
